import { useCallback, useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  googleAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { Run, RunStatus } from './RunPanel';
import { CALLER_ID_TOKEN_HEADER } from './callerToken';

/** Plain language, because "awaiting_approval" is not a sentence. */
export const STATUS_LABEL: Record<RunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  awaiting_approval: 'Waiting for DevOps',
  succeeded: 'Done',
  failed: 'Failed',
};

export const STATUS_COLOUR: Record<RunStatus, string> = {
  queued: 'var(--ar-fg-3)',
  running: '#4c8dff',
  awaiting_approval: '#d29922',
  succeeded: '#3fb950',
  failed: '#f85149',
};

/**
 * The signed-in person's Control runs — what My requests lists, and what Home
 * shows as "Your requests".
 *
 * Unlike the manifest and placement reads, this one genuinely needs the
 * caller: the backend filters runs to the token's own email, so there is no
 * answer to give without knowing who is asking. What it must NOT do is send an
 * empty token and surface Control's `missing … token` at the user —
 * that reads as a server fault for something that is a sign-in problem, hence
 * `needsIdentity`.
 */
export function useMyRuns() {
  // Never prompts on load. Asking for the token on mount opened Google's
  // "Login Required" dialog over the whole page for anyone whose Google
  // session had lapsed (and for every Guest) — on Home, to fetch a side panel.
  // Optional means "use a session if there is one, otherwise come back
  // empty"; `signIn` is the prompt, called from a click, which is also the one
  // moment a browser reliably allows the popup.
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const googleAuth = useApi(googleAuthApiRef);

  const [runs, setRuns] = useState<Run[]>();
  const [error, setError] = useState<Error>();
  /** Signed in to Backstage, but no Google identity to filter runs by. */
  const [needsIdentity, setNeedsIdentity] = useState(false);
  /** Bumped by signIn, to read again with the new session. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const idToken = await googleAuth.getIdToken({ optional: true });
        if (!idToken) {
          if (!cancelled) setNeedsIdentity(true);
          return;
        }
        if (!cancelled) setNeedsIdentity(false);
        const baseUrl = await discovery.getBaseUrl('control');
        const response = await fetchApi.fetch(`${baseUrl}/runs`, {
          headers: { [CALLER_ID_TOKEN_HEADER]: idToken },
        });
        if (!response.ok) {
          throw new Error(`${response.status} ${await response.text()}`);
        }
        const body = (await response.json()) as { runs: Run[] };
        if (!cancelled) setRuns(body.runs);
      } catch (e) {
        if (!cancelled) setError(e as Error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discovery, fetchApi, googleAuth, attempt]);

  const signIn = useCallback(async () => {
    try {
      await googleAuth.getIdToken();
      setError(undefined);
      setAttempt(a => a + 1);
    } catch (e) {
      // Rejected only when the person dismisses the sign-in; nothing to say.
    }
  }, [googleAuth]);

  return {
    runs,
    error,
    needsIdentity,
    signIn,
    loading: !runs && !error && !needsIdentity,
  };
}
